import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUserPlus, FaEdit, FaUserSlash, FaUserCheck, FaTrash, FaSearch, FaCheckCircle, FaExclamationCircle, FaTimes, FaEye } from 'react-icons/fa';
import { fetchAuth } from '../../utils/api';
import './GestionUsuarios.css';

function GestionUsuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState('TODOS');
  const [busqueda, setBusqueda] = useState('');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('crear'); // 'crear' o 'editar'
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ visible: false, type: 'success', title: '', message: '' });
  
  // Form states
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    rut: '',
    id_rol: '',
    id_tarifa: ''
  });
  const [tarifas, setTarifas] = useState([]);
  const [tarifasLoading, setTarifasLoading] = useState(false);
  const [tarifasError, setTarifasError] = useState(null);
  const [tarifaDropdownOpen, setTarifaDropdownOpen] = useState(false);
  const tarifaRef = React.useRef(null);
  const [rolDropdownOpen, setRolDropdownOpen] = useState(false);
  const rolRef = React.useRef(null);
  const [formErrors, setFormErrors] = useState({});
  const [confirmDarBajaOpen, setConfirmDarBajaOpen] = useState(false);
  const [usuarioParaBaja, setUsuarioParaBaja] = useState(null);
  const [confirmReactivarOpen, setConfirmReactivarOpen] = useState(false);
  const [usuarioParaReactivar, setUsuarioParaReactivar] = useState(null);
  const [confirmEliminarOpen, setConfirmEliminarOpen] = useState(false);
  const [usuarioParaEliminar, setUsuarioParaEliminar] = useState(null);
  const [eliminarConfirmText, setEliminarConfirmText] = useState('');
  const [eliminarInputError, setEliminarInputError] = useState('');

  // Detail Modal State
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const splitFullName = (full) => {
    const s = String(full || '').trim().split(/\s+/);
    const nombre = s[0] || '';
    const apellido = s.slice(1).join(' ') || '';
    return { nombre, apellido };
  };

  // Validación de email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validación de RUT relajada (solo caracteres permitidos)
  const validateRUT = (rut) => {
    if (!rut) return true;
    const cleanRUT = rut.replace(/[.-]/g, '');
    return /^[0-9Kk]+$/.test(cleanRUT);
  };

  // Formatear RUT mientras se escribe
  const formatRUT = (value) => {
    const cleaned = value.replace(/[^0-9Kk]/g, '');
    if (cleaned.length <= 1) return cleaned;
    
    const body = cleaned.slice(0, -1);
    const dv = cleaned.slice(-1).toUpperCase();
    
    // Agregar puntos cada 3 dígitos
    const formatted = body.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
    return `${formatted}-${dv}`;
  };

  // Validar formulario completo
  const validateForm = () => {
    const errors = {};
    
    if (!formData.nombre.trim()) {
      errors.nombre = 'El nombre es requerido';
    } else if (formData.nombre.trim().length < 3) {
      errors.nombre = 'El nombre debe tener al menos 3 caracteres';
    }
    
    if (modalMode === 'crear') {
      if (!formData.apellido.trim()) {
        errors.apellido = 'El apellido es requerido';
      } else if (formData.apellido.trim().length < 2) {
        errors.apellido = 'El apellido debe tener al menos 2 caracteres';
      }
    }
    
    if (!formData.email.trim()) {
      errors.email = 'El correo electrónico es requerido';
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Ingresa un correo electrónico válido';
    }
    
    if (formData.rut && !validateRUT(formData.rut)) {
      errors.rut = 'RUT inválido. Usa formato 12.345.678-9 y dígito verificador correcto';
    }
    
    if (!formData.id_rol) {
      errors.id_rol = 'Debes seleccionar un rol';
    }
    if (String(formData.id_rol) === '1') {
      if (!String(formData.id_tarifa || '').trim()) {
        errors.id_tarifa = 'Debes seleccionar una tarifa';
      }
    }
    
    setFormErrors(errors);
    return errors;
  };

  useEffect(() => {
    fetchUsuarios();
  }, []);

  useEffect(() => {
    if (!toast.visible) return;
    const t = setTimeout(() => {
      setToast(prev => ({ ...prev, visible: false }));
    }, 3000);
    return () => clearTimeout(t);
  }, [toast.visible]);

  const fetchTarifas = async () => {
    try {
      setTarifasLoading(true);
      setTarifasError(null);
      const data = await fetchAuth('/api/admin/tarifas/');
      setTarifas(data.tarifas || []);
    } catch (err) {
      setTarifasError(err.message || 'Error al cargar tarifas');
    } finally {
      setTarifasLoading(false);
    }
  };

  const tarifaLabel = () => {
    const t = tarifas.find(x => String(x.id_tarifa) === String(formData.id_tarifa));
    if (!t) return 'Seleccionar tarifa';
    return `${t.nombre_tarifa} — ${t.monto} ${t.moneda}`;
  };

  const rolLabel = () => {
    const v = String(formData.id_rol || '');
    if (!v) return 'Seleccionar rol';
    return v === '1' ? 'Colaborador' : v === '2' ? 'Jefe de Proyecto' : 'Seleccionar rol';
  };

  useEffect(() => {
    const onClickOutside = (e) => {
      if (tarifaRef.current && !tarifaRef.current.contains(e.target)) {
        setTarifaDropdownOpen(false);
      }
      if (rolRef.current && !rolRef.current.contains(e.target)) {
        setRolDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const fetchUsuarios = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAuth('/api/admin/usuarios/');
      setUsuarios(data.usuarios || []);
    } catch (err) {
      console.error('Error usuarios:', err);
      if (err.status === 403) {
        setError('Acceso denegado. Inicia sesión con una cuenta de Administrador.');
      } else {
        setError(err.message || 'Error al cargar usuarios');
      }
    } finally {
      setLoading(false);
    }
  };


  const showToast = (type, title, message) => {
    setToast({ visible: true, type, title, message });
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(String(text || ''));
      return true;
    } catch {
      return false;
    }
  };

  const handleCrearUsuario = async (e) => {
    e.preventDefault();
    
    // Validar formulario y mostrar el primer error de forma clara
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      const order = ['id_rol', 'id_tarifa', 'nombre', 'apellido', 'email', 'rut'];
      const firstKey = order.find(k => errors[k]);
      const msg = errors[firstKey] || 'Por favor corrige los errores en el formulario';
      showToast('error', 'Formulario incompleto', msg);
      return;
    }
    
    try {
      setSubmitting(true);
      const redirectTo = `${window.location.origin}/auth/complete-invite`;
      const nombreCompleto = `${formData.nombre.trim()} ${formData.apellido.trim()}`.replace(/\s+/g, ' ').trim();
      const payload = {
        nombre: nombreCompleto,
        email: formData.email.trim().toLowerCase(),
        rut: formData.rut ? formData.rut.replace(/[.-]/g, '') : null,
        id_rol: formData.id_rol ? Number(formData.id_rol) : formData.id_rol,
        redirect_to: redirectTo
      };
      if (String(formData.id_rol) === '1' && String(formData.id_tarifa || '').trim()) {
        payload.id_tarifa = Number(formData.id_tarifa);
      }
      const res = await fetchAuth('/api/admin/usuarios/crear/', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      await fetchUsuarios();
      handleCloseModal();
      if (res && res.warning) {
        showToast('error', 'Usuario creado parcialmente', String(res.warning || 'Fallo al enviar el correo'));
      } else {
        if (res && res.action_link) {
          const copied = await copyToClipboard(res.action_link);
          if (copied) {
            showToast('success', 'Usuario creado', 'Invitación generada. El enlace se copió al portapapeles.');
          } else {
            showToast('success', 'Usuario creado', 'Invitación generada. Copia el enlace desde la consola.');
            console.log('Invitación generada:', res.action_link);
          }
        } else {
          showToast('success', 'Usuario creado', 'Se envió una invitación por email para definir su contraseña.');
        }
      }
    } catch (err) {
      showToast('error', 'No se pudo crear el usuario', err.message || 'Intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleActualizarUsuario = async (e) => {
    e.preventDefault();
    
    // Validar formulario (sin email en edición)
    const errors = {};
    if (!formData.nombre.trim()) {
      errors.nombre = 'El nombre es requerido';
    }
    if (formData.rut && !validateRUT(formData.rut)) {
      errors.rut = 'RUT inválido';
    }
    if (!formData.id_rol) {
      errors.id_rol = 'Debes seleccionar un rol';
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      showToast('error', 'Formulario incompleto', 'Por favor corrige los errores en el formulario');
      return;
    }
    
    try {
      setSubmitting(true);
      const payload = {
        nombre: `${formData.nombre.trim()} ${formData.apellido.trim()}`.replace(/\s+/g, ' ').trim(),
        id_rol: Number(formData.id_rol),
        rut: formData.rut ? formData.rut.replace(/[.-]/g, '') : null
      };
      if (String(formData.id_rol) === '1' && String(formData.id_tarifa || '').trim()) {
        payload.id_tarifa = Number(formData.id_tarifa);
      }
      
      await fetchAuth(`/api/admin/usuarios/${usuarioSeleccionado.id_usuario}/actualizar/`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      await fetchUsuarios();
      handleCloseModal();
      showToast('success', 'Usuario actualizado', 'Los cambios fueron guardados.');
    } catch (err) {
      showToast('error', 'No se pudo actualizar', err.message || 'Intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDarDeBaja = (usuario) => {
    setUsuarioParaBaja(usuario);
    setConfirmDarBajaOpen(true);
  };

  const closeConfirmDarBaja = () => {
    setConfirmDarBajaOpen(false);
    setUsuarioParaBaja(null);
  };

  const confirmDarBaja = async () => {
    if (!usuarioParaBaja) return;
    try {
      setSubmitting(true);
      await fetchAuth(`/api/admin/usuarios/${usuarioParaBaja.id_usuario}/dar-baja/`, { method: 'PUT' });
      await fetchUsuarios();
      closeConfirmDarBaja();
      showToast('success', 'Usuario desactivado', 'El usuario fue dado de baja.');
    } catch (err) {
      showToast('error', 'No se pudo desactivar', err.message || 'Intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReactivar = (usuario) => {
    setUsuarioParaReactivar(usuario);
    setConfirmReactivarOpen(true);
  };

  const closeConfirmReactivar = () => {
    setConfirmReactivarOpen(false);
    setUsuarioParaReactivar(null);
  };

  const confirmReactivar = async () => {
    if (!usuarioParaReactivar) return;
    try {
      setSubmitting(true);
      await fetchAuth(`/api/admin/usuarios/${usuarioParaReactivar.id_usuario}/reactivar/`, { method: 'PUT' });
      await fetchUsuarios();
      closeConfirmReactivar();
      showToast('success', 'Usuario reactivado', 'El usuario fue reactivado.');
    } catch (err) {
      showToast('error', 'No se pudo reactivar', err.message || 'Intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEliminarPermanente = (usuario) => {
    setUsuarioParaEliminar(usuario);
    setEliminarConfirmText('');
    setEliminarInputError('');
    setConfirmEliminarOpen(true);
  };

  const closeConfirmEliminar = () => {
    setConfirmEliminarOpen(false);
    setUsuarioParaEliminar(null);
    setEliminarConfirmText('');
    setEliminarInputError('');
  };

  const confirmEliminar = async () => {
    if (!usuarioParaEliminar) return;
    if (eliminarConfirmText !== 'ELIMINAR') {
      setEliminarInputError('Escribe "ELIMINAR" para confirmar');
      return;
    }
    try {
      setSubmitting(true);
      await fetchAuth(`/api/admin/usuarios/${usuarioParaEliminar.id_usuario}/eliminar-permanente/`, { method: 'DELETE' });
      await fetchUsuarios();
      closeConfirmEliminar();
      showToast('success', 'Usuario eliminado', 'El usuario fue eliminado permanentemente.');
    } catch (err) {
      showToast('error', 'No se pudo eliminar', err.message || 'Intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const fetchUserDetail = async (id_usuario) => {
    try {
      setDetailLoading(true);
      setDetailError(null);
      const data = await fetchAuth(`/api/admin/usuarios/${id_usuario}/detalle/`);
      setDetailData(data.usuario);
    } catch (err) {
      setDetailError(err.message || 'Error al cargar detalles del usuario');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleOpenDetail = (usuario) => {
    setDetailData(null);
    setShowDetailModal(true);
    fetchUserDetail(usuario.id_usuario);
  };

  const handleCloseDetail = () => {
    setShowDetailModal(false);
    setDetailData(null);
    setDetailError(null);
  };

  const handleOpenModal = (mode, usuario = null) => {
    setModalMode(mode);
    setUsuarioSeleccionado(usuario);
    setFormErrors({}); // Limpiar errores previos
    
    if (mode === 'editar' && usuario) {
      console.log('📝 Editando usuario:', usuario);
      console.log('📝 id_rol:', usuario.id_rol, 'tipo:', typeof usuario.id_rol);
      const parts = splitFullName(usuario.nombre);
      setFormData({
        nombre: parts.nombre,
        apellido: parts.apellido,
        email: usuario.email,
        rut: usuario.rut ? formatRUT(usuario.rut) : '',
        id_rol: String(usuario.id_rol),
        id_tarifa: String(
          (usuario.id_tarifa ?? (usuario.tarifa ? usuario.tarifa.id_tarifa : '')) || ''
        )
      });
    } else {
      setFormData({
        nombre: '',
        apellido: '',
        email: '',
        rut: '',
        id_rol: '',
        id_tarifa: ''
      });
    }
    
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setUsuarioSeleccionado(null);
    setFormErrors({});
    setFormData({
      nombre: '',
      apellido: '',
      email: '',
      rut: '',
      id_rol: '',
      id_tarifa: ''
    });
  };

  const handleInputChange = (field, value) => {
    if (field === 'rut') {
      // Formatear RUT mientras se escribe
      setFormData({ ...formData, rut: formatRUT(value) });
    } else if (field === 'email') {
      // Convertir email a minúsculas
      setFormData({ ...formData, email: value.toLowerCase() });
    } else if (field === 'id_rol') {
      const newRol = value;
      setFormData({ ...formData, id_rol: newRol, id_tarifa: String(newRol) === '1' ? formData.id_tarifa : '' });
      if (String(newRol) === '1' && tarifas.length === 0 && !tarifasLoading) {
        fetchTarifas();
      }
    } else {
      setFormData({ ...formData, [field]: value });
    }
    
    // Limpiar error del campo al escribir
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: '' });
    }
  };

  // Filtrado
  const usuariosFiltrados = usuarios.filter(usuario => {
    const matchEstado = filtroEstado === 'TODOS' || usuario.estado === filtroEstado;
    const matchBusqueda = usuario.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
                          usuario.email.toLowerCase().includes(busqueda.toLowerCase()) ||
                          (usuario.rut && usuario.rut.toLowerCase().includes(busqueda.toLowerCase()));
    return matchEstado && matchBusqueda;
  });
  const jefesFiltrados = usuariosFiltrados.filter(u => String((u.rol_nombre||'')).toUpperCase()==='JEFE');
  const colaboradoresFiltrados = usuariosFiltrados.filter(u => String((u.rol_nombre||'')).toUpperCase()==='COLABORADOR');

  if (loading) {
    return (
      <div className="gestion-usuarios">
        <div className="loading-spinner">Cargando usuarios...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gestion-usuarios">
        <div className="error-message">
          <p>❌ {error}</p>
          <button onClick={fetchUsuarios}>Reintentar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="gestion-usuarios">
      <AnimatePresence>
        {toast.visible && (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50">
            <motion.div
              initial={{ opacity: 0, y: -16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -16, scale: 0.98 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
            >
              <div className={`flex items-start gap-3 rounded-xl border shadow-lg px-4 py-3 ${toast.type === 'success' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                <div className={`${toast.type === 'success' ? 'text-emerald-600' : 'text-red-600'} text-xl`}>
                  {toast.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-800">{toast.title}</p>
                  <p className="text-sm text-slate-600">{toast.message}</p>
                </div>
                <button
                  onClick={() => setToast(prev => ({ ...prev, visible: false }))}
                  className="text-slate-400 hover:text-slate-600"
                  aria-label="Cerrar notificación"
                >
                  <FaTimes />
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      <div className="usuarios-header">
        <h1>Gestión de Usuarios</h1>
        <div style={{display: 'flex', gap: '10px'}}>
          <button className="btn-crear" onClick={() => handleOpenModal('crear')}>
            <FaUserPlus /> Crear Usuario
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="usuarios-filtros">
        <div className="busqueda">
          <FaSearch />
          <input
            type="text"
            placeholder="Buscar por nombre, email o RUT..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        
        <div className="filtro-estado">
          <button 
            className={filtroEstado === 'TODOS' ? 'active' : ''}
            onClick={() => setFiltroEstado('TODOS')}
          >
            Todos ({usuarios.length})
          </button>
          <button 
            className={filtroEstado === 'ACTIVO' ? 'active' : ''}
            onClick={() => setFiltroEstado('ACTIVO')}
          >
            Activos ({usuarios.filter(u => u.estado === 'ACTIVO').length})
          </button>
          <button 
            className={filtroEstado === 'INACTIVO' ? 'active' : ''}
            onClick={() => setFiltroEstado('INACTIVO')}
          >
            Inactivos ({usuarios.filter(u => u.estado === 'INACTIVO').length})
          </button>
        </div>

        
      </div>

      <h2 className="subsection-title">Jefes de Proyectos</h2>
      {/* Tabla Jefes */}
      <div className="usuarios-tabla-container">
        <table className="usuarios-tabla">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre</th>
              <th>Email</th>
              <th>RUT</th>
              <th>Rol</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {jefesFiltrados.map(usuario => (
              <tr key={`jefe-${usuario.id_usuario}`}>
                <td>{usuario.id_usuario}</td>
                <td>{usuario.nombre}</td>
                <td>{usuario.email}</td>
                <td>{usuario.rut ? formatRUT(usuario.rut) : '-'}</td>
                <td>
                  <span className={`rol-badge rol-${usuario.rol_nombre?.toLowerCase()}`}>
                    {usuario.rol_nombre || 'Sin rol'}
                  </span>
                </td>
                <td>
                  <span className={`estado-badge estado-${usuario.estado?.toLowerCase()}`}>
                    {usuario.estado || 'ACTIVO'}
                  </span>
                </td>
                <td className="acciones">
                  <button className="btn-editar" style={{backgroundColor: '#f1f5f9', color: '#475569'}} onClick={() => handleOpenDetail(usuario)} title="Ver detalle" aria-label="Ver detalle del usuario">
                    <FaEye />
                    <span className="btn-label">Ver detalle</span>
                  </button>
                  <button className="btn-editar" onClick={() => handleOpenModal('editar', usuario)} title="Editar" aria-label="Editar usuario">
                    <FaEdit />
                    <span className="btn-label">Editar</span>
                  </button>
                  {usuario.estado === 'ACTIVO' ? (
                    <button className="btn-desactivar" onClick={() => handleDarDeBaja(usuario)} title="Dar de baja" aria-label="Desactivar usuario">
                      <FaUserSlash />
                    </button>
                  ) : (
                    <button className="btn-reactivar" onClick={() => handleReactivar(usuario)} title="Reactivar" aria-label="Reactivar usuario">
                      <FaUserCheck />
                      <span className="btn-label">Reactivar</span>
                    </button>
                  )}
                  <button className="btn-eliminar" onClick={() => handleEliminarPermanente(usuario)} title="Eliminar permanentemente" aria-label="Eliminar usuario">
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {jefesFiltrados.length === 0 && (
          <div className="no-resultados"><p>No hay jefes para los filtros aplicados</p></div>
        )}
      </div>

      <h2 className="subsection-title" style={{marginTop: '1rem'}}>Colaboradores Externos</h2>
      {/* Tabla Colaboradores */}
      <div className="usuarios-tabla-container" style={{marginTop: '1rem'}}>
        <table className="usuarios-tabla">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre</th>
              <th>Email</th>
              <th>RUT</th>
              <th>Tarifa</th>
              <th>Rol</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {colaboradoresFiltrados.map(usuario => (
              <tr key={`col-${usuario.id_usuario}`}>
                <td>{usuario.id_usuario}</td>
                <td>{usuario.nombre}</td>
                <td>{usuario.email}</td>
                <td>{usuario.rut ? formatRUT(usuario.rut) : '-'}</td>
                <td>{usuario.tarifa && usuario.tarifa.nombre_tarifa ? `${usuario.tarifa.nombre_tarifa} ($${Number(usuario.tarifa.monto || 0).toLocaleString('es-CL')}/h)` : (typeof usuario.tarifa === 'string' ? usuario.tarifa : '-')}</td>
                <td>
                  <span className={`rol-badge rol-${usuario.rol_nombre?.toLowerCase()}`}>
                    {usuario.rol_nombre || 'Sin rol'}
                  </span>
                </td>
                <td>
                  <span className={`estado-badge estado-${usuario.estado?.toLowerCase()}`}>
                    {usuario.estado || 'ACTIVO'}
                  </span>
                </td>
                <td className="acciones">
                  <button className="btn-editar" style={{backgroundColor: '#f1f5f9', color: '#475569'}} onClick={() => handleOpenDetail(usuario)} title="Ver detalle" aria-label="Ver detalle del usuario">
                    <FaEye />
                    <span className="btn-label">Ver detalle</span>
                  </button>
                  <button className="btn-editar" onClick={() => handleOpenModal('editar', usuario)} title="Editar" aria-label="Editar usuario">
                    <FaEdit />
                    <span className="btn-label">Editar</span>
                  </button>
                  {usuario.estado === 'ACTIVO' ? (
                    <button className="btn-desactivar" onClick={() => handleDarDeBaja(usuario)} title="Dar de baja" aria-label="Desactivar usuario">
                      <FaUserSlash />
                    </button>
                  ) : (
                    <button className="btn-reactivar" onClick={() => handleReactivar(usuario)} title="Reactivar" aria-label="Reactivar usuario">
                      <FaUserCheck />
                      <span className="btn-label">Reactivar</span>
                    </button>
                  )}
                  <button className="btn-eliminar" onClick={() => handleEliminarPermanente(usuario)} title="Eliminar permanentemente" aria-label="Eliminar usuario">
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {colaboradoresFiltrados.length === 0 && (
          <div className="no-resultados"><p>No hay colaboradores para los filtros aplicados</p></div>
        )}
      </div>

      {/* Modal */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2>{modalMode === 'crear' ? 'Crear Usuario' : 'Editar Usuario'}</h2>
              </div>
            
            <form onSubmit={modalMode === 'crear' ? handleCrearUsuario : handleActualizarUsuario}>
              <div className="form-group" ref={rolRef}>
                <label>Rol *</label>
                <div
                  className={`select-custom ${formErrors.id_rol ? 'input-error' : ''}`}
                  onClick={() => setRolDropdownOpen(!rolDropdownOpen)}
                  role="button"
                  aria-haspopup="listbox"
                  aria-expanded={rolDropdownOpen}
                >
                  <span className={`select-value ${!formData.id_rol ? 'placeholder' : ''}`}>{rolLabel()}</span>
                  <span className="select-arrow" aria-hidden>▾</span>
                </div>
                {rolDropdownOpen && (
                  <div className="select-menu" role="listbox">
                    <div className="select-menu-header">Seleccionar rol</div>
                    <div
                      className={`select-option ${String(formData.id_rol)==='1'?'active':''}`}
                      role="option"
                      aria-selected={String(formData.id_rol)==='1'}
                      onClick={() => { handleInputChange('id_rol','1'); setRolDropdownOpen(false); }}
                    >
                      Colaborador
                    </div>
                    <div
                      className={`select-option ${String(formData.id_rol)==='2'?'active':''}`}
                      role="option"
                      aria-selected={String(formData.id_rol)==='2'}
                      onClick={() => { handleInputChange('id_rol','2'); setRolDropdownOpen(false); }}
                    >
                      Jefe de Proyecto
                    </div>
                  </div>
                )}
                {formErrors.id_rol && <span className="error-text">{formErrors.id_rol}</span>}
              </div>

              {String(formData.id_rol) === '1' && (
                <div className="form-group" ref={tarifaRef}>
                  <label>Tarifa</label>
                  <div
                    className={`select-custom ${formErrors.id_tarifa ? 'input-error' : ''}`}
                    onClick={() => {
                      if (!tarifasLoading && !tarifasError) setTarifaDropdownOpen(!tarifaDropdownOpen);
                      if (tarifas.length === 0 && !tarifasLoading) fetchTarifas();
                    }}
                    role="button"
                    aria-haspopup="listbox"
                    aria-expanded={tarifaDropdownOpen}
                  >
                    <span className={`select-value ${!formData.id_tarifa ? 'placeholder' : ''}`}>{tarifaLabel()}</span>
                    <span className="select-arrow" aria-hidden>▾</span>
                  </div>
                  {tarifaDropdownOpen && (
                    <div className="select-menu" role="listbox">
                      <div className="select-menu-header">Seleccionar tarifa</div>
                      {tarifas.map(t => (
                        <div
                          key={t.id_tarifa}
                          className={`select-option ${String(formData.id_tarifa)===String(t.id_tarifa)?'active':''}`}
                          role="option"
                          aria-selected={String(formData.id_tarifa)===String(t.id_tarifa)}
                          onClick={() => {
                            handleInputChange('id_tarifa', String(t.id_tarifa));
                            setTarifaDropdownOpen(false);
                          }}
                        >
                          {t.nombre_tarifa} — {t.monto} {t.moneda}
                        </div>
                      ))}
                      {tarifasLoading && <div className="select-menu-footer">Cargando tarifas...</div>}
                      {tarifasError && <div className="select-menu-footer error">{tarifasError}</div>}
                    </div>
                  )}
                  {formErrors.id_tarifa && <span className="error-text">{formErrors.id_tarifa}</span>}
                </div>
              )}
              <div className="form-group">
                <label>Nombre *</label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => handleInputChange('nombre', e.target.value)}
                  required
                  className={formErrors.nombre ? 'input-error' : ''}
                />
                {formErrors.nombre && <span className="error-text">{formErrors.nombre}</span>}
              </div>

              <div className="form-group">
                <label>{modalMode === 'crear' ? 'Apellido *' : 'Apellido'}</label>
                <input
                  type="text"
                  value={formData.apellido}
                  onChange={(e) => handleInputChange('apellido', e.target.value)}
                  required={modalMode === 'crear'}
                  className={formErrors.apellido ? 'input-error' : ''}
                />
                {formErrors.apellido && <span className="error-text">{formErrors.apellido}</span>}
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                  disabled={modalMode === 'editar'}
                  className={formErrors.email ? 'input-error' : ''}
                />
                {formErrors.email && <span className="error-text">{formErrors.email}</span>}
                {modalMode === 'editar' && (
                  <small style={{color: '#64748b', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block'}}>
                    El email no puede ser modificado
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>RUT</label>
                <input
                  type="text"
                  value={formData.rut}
                  onChange={(e) => handleInputChange('rut', e.target.value)}
                  placeholder="xx.xxx.xxx-1"
                  maxLength="12"
                  className={formErrors.rut ? 'input-error' : ''}
                />
                {formErrors.rut && <span className="error-text">{formErrors.rut}</span>}
                <small style={{color: '#64748b', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block'}}>
                  Formato: 12.345.678-9 (opcional)
                </small>
              </div>

              

              {/* Nota: Ya no se solicita contraseña. Se envía invitación por correo para que el usuario la defina. */}
              {modalMode === 'crear' && (
                <div className="form-group">
                  <small style={{color: '#64748b'}}>
                    Se enviará una invitación por email para que el usuario establezca su contraseña.
                  </small>
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn-cancelar" onClick={handleCloseModal} disabled={submitting}>
                  Cancelar
                </button>
                <button type="submit" className="btn-guardar" disabled={submitting}>
                  {submitting ? 'Guardando...' : (modalMode === 'crear' ? 'Crear Usuario' : 'Guardar Cambios')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {confirmDarBajaOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Dar de baja usuario</h2>
            </div>
            <div className="p-3">
              <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                <div className="text-red-600 text-xl">
                  <FaExclamationCircle />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-800">¿Estás seguro?</p>
                  <p className="text-sm text-slate-600">Se desactivará el acceso de <strong>{usuarioParaBaja?.nombre}</strong>. Podrás reactivarlo luego.</p>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-cancelar" onClick={closeConfirmDarBaja} disabled={submitting}>Cancelar</button>
              <button type="button" className="btn-guardar" onClick={confirmDarBaja} disabled={submitting}>{submitting ? 'Procesando...' : 'Dar de baja'}</button>
            </div>
          </div>
        </div>
      )}
      {confirmReactivarOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Reactivar usuario</h2>
            </div>
            <div className="p-3">
              <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                <div className="text-emerald-600 text-xl">
                  <FaCheckCircle />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-800">Confirmar reactivación</p>
                  <p className="text-sm text-slate-600">Se reactivará el acceso de <strong>{usuarioParaReactivar?.nombre}</strong>.</p>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-cancelar" onClick={closeConfirmReactivar} disabled={submitting}>Cancelar</button>
              <button type="button" className="btn-guardar" onClick={confirmReactivar} disabled={submitting}>{submitting ? 'Procesando...' : 'Reactivar'}</button>
            </div>
          </div>
        </div>
      )}
      {confirmEliminarOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Eliminar permanentemente</h2>
            </div>
            <div className="p-3">
              <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                <div className="text-red-600 text-xl">
                  <FaExclamationCircle />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-800">Acción irreversible</p>
                  <p className="text-sm text-slate-600">Se eliminarán todos los datos de <strong>{usuarioParaEliminar?.nombre}</strong>. Escribe <strong>ELIMINAR</strong> para confirmar.</p>
                </div>
              </div>
              <div className="form-group" style={{ marginTop: '0.75rem' }}>
                <input
                  type="text"
                  value={eliminarConfirmText}
                  onChange={(e) => { setEliminarConfirmText(e.target.value); setEliminarInputError(''); }}
                  placeholder="ELIMINAR"
                  className={eliminarInputError ? 'input-error' : ''}
                />
                {eliminarInputError && <span className="error-text">{eliminarInputError}</span>}
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-cancelar" onClick={closeConfirmEliminar} disabled={submitting}>Cancelar</button>
              <button type="button" className="btn-guardar" onClick={confirmEliminar} disabled={submitting}>{submitting ? 'Eliminando...' : 'Eliminar'}</button>
            </div>
          </div>
        </div>
      )}
      {showDetailModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{maxWidth: '500px'}}>
            <div className="modal-header">
              <h2>Detalle de Usuario</h2>
            </div>
            <div className="p-6">
              {detailLoading ? (
                <div className="flex justify-center py-8">
                  <div className="loading-spinner"></div>
                </div>
              ) : detailError ? (
                <div className="text-red-600 text-center py-4">
                  {detailError}
                </div>
              ) : detailData ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">Nombre</label>
                      <p className="text-slate-900 font-medium">{detailData.nombre}</p>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">RUT</label>
                      <p className="text-slate-900 font-mono">{detailData.rut ? formatRUT(detailData.rut) : '—'}</p>
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">Email</label>
                      <p className="text-slate-900">{detailData.email}</p>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">Rol</label>
                      <span className={`rol-badge rol-${detailData.rol?.toLowerCase()}`}>
                        {detailData.rol || 'Sin rol'}
                      </span>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">Estado</label>
                      <span className={`estado-badge estado-${detailData.estado?.toLowerCase()}`}>
                        {detailData.estado || 'ACTIVO'}
                      </span>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">Teléfono</label>
                      <p className="text-slate-900">{detailData.telefono || '—'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">Tel. Emergencia</label>
                      <p className="text-slate-900">{detailData.telefono_emergencia || '—'}</p>
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">Dirección</label>
                      <p className="text-slate-900">{detailData.direccion || '—'}</p>
                    </div>
                  </div>
                  
                  {detailData.tarifa && detailData.tarifa.nombre_tarifa && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <h4 className="text-sm font-semibold text-slate-800 mb-3">Información de Tarifa</h4>
                      <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-indigo-900 font-medium">{detailData.tarifa.nombre_tarifa}</span>
                          <span className="text-indigo-700 font-bold">
                            {detailData.tarifa.monto} {detailData.tarifa.moneda}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-guardar" onClick={handleCloseDetail}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GestionUsuarios;
